import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CrosswalkProgressBar = ({
  status,
  completedCount = 0,
  runningCount = 0,
  failedCount = 0,
  noProposalCount = 0,  // Treat no_proposal as completed
  total = 0,
  loading = false
}) => {
  const barRef = useRef(null);
  // Tooltip is rendered in a portal (fixed to the viewport) so it can never be
  // clipped by the table's overflow-x-auto scroll container.
  const [tooltip, setTooltip] = useState(null); // { x, y, below }

  // While the row's crosswalk status is still streaming in over SSE (e.g. right after a
  // tab switch remounts the page), show a skeleton bar instead of an empty gray bar so
  // the user knows the status is refreshing rather than "not started".
  if (loading) {
    return (
      <div className="relative w-full">
        <div className="relative w-full h-2 bg-[#919191]/20 rounded-full overflow-hidden">
          <div className="absolute inset-0 crosswalk-skeleton-wave" />
        </div>
        <style jsx>{`
          @keyframes crosswalkSkeletonWave {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
          .crosswalk-skeleton-wave {
            background-image: linear-gradient(
              90deg,
              transparent 0%,
              rgba(145, 145, 145, 0.75) 50%,
              transparent 100%
            );
            animation: crosswalkSkeletonWave 0.7s linear infinite;
          }
        `}</style>
      </div>
    );
  }

  // Combine completed and no_proposal counts (both are terminal success states)
  const effectiveCompletedCount = completedCount + noProposalCount;

  // Not started count = questionnaires not yet in any active state
  const notStartedCount = Math.max(0, total - effectiveCompletedCount - runningCount - failedCount);

  // Calculate percentages
  const completedPct = total > 0 ? (effectiveCompletedCount / total) * 100 : 0;
  const runningPct = total > 0 ? (runningCount / total) * 100 : 0;
  const failedPct = total > 0 ? (failedCount / total) * 100 : 0;
  const notStartedPct = total > 0 ? (notStartedCount / total) * 100 : 0;

  const TOOLTIP_SPACE = 150; // approx tooltip height + gap

  const showTooltip = () => {
    const el = barRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Flip below the bar if there isn't enough room above it in the viewport
    const below = rect.top < TOOLTIP_SPACE;
    setTooltip({
      x: rect.left + rect.width / 2,
      y: below ? rect.bottom + 12 : rect.top - 12,
      below,
    });
  };

  const hideTooltip = () => setTooltip(null);

  const tooltipContent = tooltip && (
    <div
      style={{
        position: 'fixed',
        left: tooltip.x,
        top: tooltip.y,
        transform: `translate(-50%, ${tooltip.below ? '0' : '-100%'})`,
        zIndex: 99999,
        pointerEvents: 'none',
      }}
    >
      <div className="relative px-4 py-2.5 bg-[#53545a] text-white text-xs rounded-lg whitespace-nowrap shadow-xl border border-[#53545a]/80">
        <div className="space-y-1">
          {total > 0 ? (
            <>
              <div className="font-semibold text-white mb-1.5 pb-1.5 border-b border-white/20">Progress Status</div>
              {effectiveCompletedCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#0077b5]" />
                  <span className="text-white/90">
                    {effectiveCompletedCount} Completed
                    {noProposalCount > 0 && completedCount > 0 && (
                      <span className="text-white/60 text-[10px] ml-1">
                        ({completedCount} + {noProposalCount} no proposals)
                      </span>
                    )}
                  </span>
                </div>
              )}
              {runningCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#F47F2E]" />
                  <span className="text-white/90">{runningCount} Running</span>
                </div>
              )}
              {failedCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-white/90">{failedCount} Failed</span>
                </div>
              )}
              {notStartedCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#919191]" />
                  <span className="text-white/90">{notStartedCount} Not Started</span>
                </div>
              )}
              <div className="pt-1.5 mt-1.5 border-t border-white/20 text-white font-medium">
                Total: {total}
              </div>
            </>
          ) : (
            <div className="text-white/90">Not Started</div>
          )}
        </div>
        {/* Tooltip arrow */}
        {tooltip.below ? (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent border-b-[#53545a]" />
        ) : (
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-[#53545a]" />
        )}
      </div>
    </div>
  );

  return (
    <div className="relative w-full">
      {/* Progress bar container with rounded edges and overflow */}
      <div
        ref={barRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="relative w-full h-2 bg-[#919191]/20 rounded-full overflow-hidden cursor-help"
      >
        {/* Progress bars stacked */}
        <div className="absolute inset-0 flex">
        {/* Completed - Blue */}
        {completedPct > 0 && (
          <div
            className="bg-[#0077b5] h-full transition-all duration-500"
            style={{ width: `${completedPct}%` }}
          />
        )}

        {/* Running - Orange with animation */}
        {runningPct > 0 && (
          <div
            className="relative bg-[#F47F2E] h-full overflow-hidden transition-all duration-500"
            style={{ width: `${runningPct}%` }}
          >
            {/* Animated shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
                 style={{
                   backgroundSize: '200% 100%',
                   animation: 'shimmer 1.5s infinite'
                 }}
            />
          </div>
        )}

        {/* Failed - Red */}
        {failedPct > 0 && (
          <div
            className="bg-red-500 h-full transition-all duration-500"
            style={{ width: `${failedPct}%` }}
          />
        )}

        {/* Not Started - Gray (fills remaining space) */}
        {notStartedPct > 0 && (
          <div
            className="bg-[#919191]/30 h-full transition-all duration-500"
            style={{ width: `${notStartedPct}%` }}
          />
        )}
      </div>
      </div>

      {/* Tooltip rendered via portal so the table's overflow can't clip it */}
      {tooltipContent && createPortal(tooltipContent, document.body)}

      {/* Add shimmer animation to styles */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
};

export default CrosswalkProgressBar;
